import { Add } from '@mui/icons-material';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import { Chip, keyframes, Stack, Typography } from '@mui/material';
import CustomIconButton from 'common/components/CustomIconButton';
import CustomTooltip from 'common/components/CustomTooltip';
import CalendarType from 'common/enums/calendarTypes';
import useFlexBoxStyles from 'common/flexBox';
import { stringToColor } from 'common/utils/utils';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { makeStyles } from 'tss-react/mui';
import ServiceFormCalendarItemCard from './ServiceFormCalendarItemCard';

const createRingPulse = (color) => keyframes`
  0% {
    box-shadow: 0 0 0 0 ${color};
  }
  70% {
    box-shadow: 0 0 0 12px rgba(0,0,0,0);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(0,0,0,0);
  }
`;

const useStyles = makeStyles()(() => {
    const red = 'rgba(255,0,0,0.7)';
    const warning = 'rgba(237, 108, 2, 0.7)';

    return {
        attentionButton: {
            position: 'relative',
            animation: `${createRingPulse(red)} 1.5s infinite`,
            border: '2px solid red',
        },
        warning: {
            position: 'relative',
            animation: `${createRingPulse(warning)} 1.5s infinite`,
            border: `2px solid ${warning}`,
        },
    };
});

const ServiceFormCalendarItem = (props) => {
    const {
        onClick,
        enableOnClick,
        calendar,
        deleteCalendar,
        deleteButton,
        editCalendar,
        editButton,
        activeCalendar,
        key,
    } = props;

    const { classes, cx } = useStyles();
    const { t } = useTranslation();
    const { classes: flexBox } = useFlexBoxStyles();

    const getAvailabilityState = (calendar) => {
        if (!calendar?.availability && calendar.type === CalendarType.AGENT) {
            return 'error';
        }

        if (!calendar?.active) {
            return 'warning';
        }

        return 'ok';
    };

    const getEditButtonClass = (calendar, classes) => {
        const state = getAvailabilityState(calendar);
        return {
            [classes.attentionButton]: state === 'error',
        };
    };

    const getEditButtonTitle = useMemo(() => {
        const state = getAvailabilityState(calendar);
        if (state === 'error') {
            return t('service.availability.selectOne');
        } else if (state === 'warning') {
            return t('service.availability.inactive');
        }
        return t('oneToOne.availability');
    }, [calendar, t]);

    const actions = (
        <>
            {editButton && (
                <CustomTooltip arrow title={getEditButtonTitle}>
                    <CustomIconButton
                        size="small"
                        onClick={() => editCalendar(calendar)}
                        className={cx(getEditButtonClass(calendar, classes))}
                    >
                        <CalendarTodayIcon />
                    </CustomIconButton>
                </CustomTooltip>
            )}
            {deleteButton && (
                <CustomTooltip arrow title={t('app.common.delete')}>
                    <CustomIconButton size="small" onClick={() => deleteCalendar(calendar)}>
                        <DeleteOutlineOutlinedIcon />
                    </CustomIconButton>
                </CustomTooltip>
            )}
            {enableOnClick && (
                <CustomTooltip arrow title={t('calendar.common.add')}>
                    <CustomIconButton size="small" onClick={onClick}>
                        <Add />
                    </CustomIconButton>
                </CustomTooltip>
            )}
        </>
    );

    const body = (
        <Stack direction="column">
            <Stack direction="column">
                {calendar.type === CalendarType.AGENT ? (
                    <CustomTooltip arrow title={calendar.email}>
                        <Typography gutterBottom component="div" noWrap className={flexBox.w100}>
                            {calendar.email}
                        </Typography>
                    </CustomTooltip>
                ) : (
                    <CustomTooltip arrow title={calendar.extraFields?.resourceId}>
                        <Chip
                            className={flexBox.wFitContent}
                            size="small"
                            style={{
                                backgroundColor: stringToColor(
                                    calendar.extraFields?.brand + calendar.extraFields?.model ||
                                        calendar.extraFields.resourceId,
                                ),
                            }}
                            label={
                                <Typography
                                    component="div"
                                    gutterBottom
                                    noWrap
                                    className={cx(flexBox.w100, flexBox.margin0)}
                                >
                                    {calendar.extraFields.resourceId}
                                </Typography>
                            }
                        />
                    </CustomTooltip>
                )}
            </Stack>
        </Stack>
    );

    return (
        <Stack m={0.5} key={key}>
            <ServiceFormCalendarItemCard
                title={calendar.name}
                image={calendar.mainImage}
                titleDivider={true}
                actions={actions}
                cardBody={body}
                actionsAlign="right"
                borderRadius={2}
                active={calendar.active}
                activeSwitch={activeCalendar}
                activeCalendar={() => activeCalendar(calendar)}
                state={getAvailabilityState(calendar)}
                enableAdd={enableOnClick}
            />
            {/* {openConfirmDeleteDlg && (
                <YesNoDialog
                    openDialog={true}
                    title={titleDlg}
                    messageToConfirm={messageDlg}
                    yesButtonCallback={() => {
                        confirmDelete();
                    }}
                    noButtonCallback={() => {
                        cancelDlg();
                    }}
                />
            )} */}
        </Stack>
    );
};

export default ServiceFormCalendarItem;
